import { useCallback, useEffect } from 'react';
import * as React from 'react';
import { noop } from 'lodash';
import Link from 'next/link';
import styled from 'styled-components';
import type { TransferJobTaskReactiveMapState } from '@frameio/graphql-client/client-schema/transfers/state/transferJobTasks';
import type { TransferJobType } from '@frameio/graphql-client/generated';
import { plural, select } from '@frameio/i18n';
import { CrustcrumbsView } from '@frameio/shared/components/Crustcrumbs';
import type { ContextMenuEvent } from '@frameio/shared/hooks/useDataContextMenu';
import { useDataContextMenu } from '@frameio/shared/hooks/useDataContextMenu';
import { getLink } from '@frameio/shared/routing/getLink';
import { TransferBatchStatus } from '@frameio/shared/transfers/states';
import { getMimeTypeFromFileExtension } from '@frameio/utils';
import {
  Box,
  LinearProgress,
  Text,
  TextWithTruncation,
  theme,
  Tooltip,
} from '@frameio/vapor';
import { IconWarningFill } from '@frameio/vapor-icons';
import type { TransferBatch } from '../../types/uploader';
import {
  getTransferBatchHasLocalData,
  getTransferBatchType,
  hasPartialFailure,
} from '../../utils';
import { ARROW_ICON_CLASSNAME } from './constants';
import TEXT from './generated/TEXT/JobCard';
import { JobControls } from './JobControls';
import { JobMediaThumbnail } from './JobMediaThumbnail';
import { JobStatus } from './JobStatus/JobStatus';
import { getTrackColor } from './utils/getTrackColor';

TEXT.DEFINE(
  {
    /** Error message when all items in a batch transfer failed */
    allFailed: (batchType: string): string =>
      select(batchType, {
        DOWNLOAD: 'All downloads failed',
        RENDER: 'All exports failed',
        other: 'All uploads failed',
      }),
    /** Error message when a single item transfer failed */
    failed: (batchType: string): string =>
      select(batchType, {
        DOWNLOAD: 'Download failed',
        RENDER: 'Export failed',
        other: 'Upload failed',
      }),
    /** Title showing count of items being transferred */
    itemsCount: (count: number): string =>
      plural(count, {
        one: '1 Item',
        other: `${count} Items`,
      }),
    /** Title showing count of folders being transferred */
    foldersCount: (count: number): string =>
      plural(count, {
        one: '1 Folder',
        other: `${count} Folders`,
      }),
    /** Tooltip text for showing failed items */
    failedTooltip: 'Show failed items',
    /** Aria label for link to navigate to project */
    navigateToProject: 'Navigate to project',
  },
  { plural, select }
);

const getJobTitle = (
  type: TransferJobType,
  name: string,
  topLevelFolderCount: number,
  topLevelFileCount: number
): string => {
  // Single Root File or Folder
  if (type !== 'BATCH') return name;

  // Root Files + Folders
  if (topLevelFolderCount > 0 && topLevelFileCount > 0) {
    return TEXT.itemsCount(topLevelFileCount + topLevelFolderCount);
  }

  // Only Root folders
  if (topLevelFolderCount > 0) {
    return TEXT.foldersCount(topLevelFolderCount);
  }

  // Only Root Files
  return TEXT.itemsCount(topLevelFileCount);
};

const JOB_CONTROLS_ICON_SIZE = 24;

export type FailedItems = TransferJobTaskReactiveMapState[];
export type SetFailedItems = (value: FailedItems) => void;

export function JobCard({
  customStatus,
  job,
  onCancelClick = noop,
  onForceFailureClick = noop,
  onRemoveClick = noop,
  onResumeClick = noop,
  onRetryClick,
  onPauseClick,
}: {
  customStatus?: string | React.ReactNode;
  job: TransferBatch;
  onCancelClick?: (id: string) => void;
  onForceFailureClick?: (id: string) => void;
  onResumeClick?: (id: string) => void;
  onRemoveClick?: (id: string, jobId: string) => void;
  onRetryClick?: (id: string) => void;
  onPauseClick?: (id: string) => void;
  setFailedItems?: SetFailedItems;
}) {
  const {
    contextMenuAnchorPosition,
    isContextMenuOpen,
    setIsContextMenuOpen,
    handleContextMenuEvent,
  } = useDataContextMenu({ dismissOnScroll: true });

  const {
    batchType,
    name,
    progress,
    status,
    topLevelFolderCount,
    topLevelFileCount,
    targetFolder,
  } = job;
  const failed = job.fileCounts?.failed || 0;
  const hasLocalData = getTransferBatchHasLocalData(job);
  const folderId = targetFolder?.id;
  const folderName = targetFolder?.name;
  const projectId = targetFolder?.project?.id;
  const projectName = targetFolder?.project?.name;
  const isAtRoot = targetFolder?.ancestorFolders?.length === 0;
  const jobType = getTransferBatchType(job);
  const jobFileExtension = name?.split('/').pop()?.split('-').pop();
  const jobMimeType = getMimeTypeFromFileExtension(jobFileExtension);
  const isTransfersComplete =
    status !== TransferBatchStatus.IDLE &&
    status !== TransferBatchStatus.PROCESSING &&
    status !== TransferBatchStatus.UPLOADING &&
    status !== TransferBatchStatus.DOWNLOADING;
  const partialFailed = hasPartialFailure(job);
  const hasFailed = status === TransferBatchStatus.FAILED && !partialFailed;
  const isCancelled = status === TransferBatchStatus.CANCELED;
  const isJobCanceling = status === TransferBatchStatus.CANCELING;

  const isOnlyFolders =
    (topLevelFolderCount ?? 0) > 0 && topLevelFileCount === 0;

  const iconType: TransferJobType = isOnlyFolders ? 'FOLDER' : jobType;
  const jobTitle = getJobTitle(
    jobType,
    name,
    topLevelFolderCount ?? 0,
    topLevelFileCount ?? 0
  );

  /**
   * NOTE: REPLACE THIS LOGIC.
   * This is a temporary derivation for downloaded file extensions, based on
   * mimeType. Eventually this should be replaced with something more robust.
   */

  const fileExtension =
    jobFileExtension || jobMimeType?.split('/').pop()?.split('-').pop();
  const mimeType = getMimeTypeFromFileExtension(fileExtension);

  const projectPath = projectId
    ? getLink({
        page: 'folder',
        projectId,
        ...(folderId && { folderAssetId: folderId }),
      })
    : '';

  const showLinearProgress =
    !isTransfersComplete && hasLocalData && !isJobCanceling;

  useEffect(function checkSyntheticError() {
    // eslint-disable-next-line no-storage/no-browser-storage
    if (sessionStorage.getItem('THROW_SYNTHETIC_UPLOAD_SHEET_ERROR')) {
      throw new Error('Upload sheet error');
    }
  }, []);

  const handleOnContextMenu = useCallback(
    (e: ContextMenuEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleContextMenuEvent(e);
    },
    [handleContextMenuEvent]
  );

  return (
    <RelativeContainer
      $isSelected={isContextMenuOpen}
      onContextMenu={handleOnContextMenu}
    >
      <Link aria-label={TEXT.navigateToProject} href={projectPath}>
        <Container data-testid="job-card">
          <Box paddingRight={12} position="relative">
            <JobMediaThumbnail
              job={job}
              mimeType={mimeType}
              status={status}
              type={iconType}
            />
          </Box>
          <Wrapper>
            <Box display="flex">
              <Box
                alignItems="center"
                display="flex"
                maxWidth={`calc(100% - ${JOB_CONTROLS_ICON_SIZE}px)`}
                paddingRight={12}
                width="100%"
              >
                <TitleContainer
                  $isCancelled={isCancelled}
                  data-testid={
                    isCancelled
                      ? 'job-card--is-cancelled'
                      : 'job-card--is-not-cancelled'
                  }
                >
                  <TextWithTruncation
                    as="h4"
                    color="primary"
                    enableTooltip={true}
                    fontWeight="medium"
                    tooltip={<Tooltip placement="top" title={jobTitle} />}
                    variant="primary"
                  >
                    {jobTitle}
                  </TextWithTruncation>
                </TitleContainer>
                <ContextWrapper>
                  {/* Failed file count. Not shown if all files failed */}
                  {partialFailed && (
                    <PillContainer>
                      <Box paddingRight={2}>
                        <IconWarningFill
                          color={theme.colors.icon.interactive.error}
                          size={16}
                        />
                      </Box>
                      <Text color="error" variant="body">
                        {failed}
                      </Text>
                    </PillContainer>
                  )}
                </ContextWrapper>
              </Box>
            </Box>
            {folderId && (
              <TitleContainer
                $isCancelled={isCancelled}
                data-testid="job-card--upload-location"
              >
                <CrustcrumbsView
                  folderName={folderName}
                  isAtRoot={isAtRoot}
                  projectName={projectName}
                />
              </TitleContainer>
            )}
            {/* When all files */}
            {hasFailed && (
              <Box display="flex" paddingTop={4}>
                <IconWarningFill
                  color={theme.colors.icon.interactive.error}
                  data-testid="failed-icon"
                  size={16}
                />
                <Box paddingLeft={4}>
                  <Text
                    color="error"
                    data-testid="failed-text"
                    variant="secondary"
                  >
                    {jobType !== 'BATCH'
                      ? TEXT.failed(batchType)
                      : TEXT.allFailed(batchType)}
                  </Text>
                </Box>
              </Box>
            )}
            {showLinearProgress && (
              <LinearProgress
                aria-label={`${name} upload progress`}
                className="progress-bar"
                value={progress?.percentage ?? 0}
                variant={getTrackColor(job)}
              />
            )}

            <Text
              as="div"
              className="status"
              color="tertiary"
              style={{ marginTop: 4 }}
              variant="secondary"
            >
              {customStatus || (
                <JobStatus
                  hasPartialFailure={partialFailed}
                  job={job}
                  status={status}
                />
              )}
            </Text>
          </Wrapper>
        </Container>
      </Link>

      <ControlsWrapper>
        <JobControls
          anchorPosition={contextMenuAnchorPosition}
          className={isContextMenuOpen ? 'active' : undefined}
          handleContextMenuEvent={handleContextMenuEvent}
          iconSize={JOB_CONTROLS_ICON_SIZE}
          isContextMenuOpen={isContextMenuOpen}
          job={job}
          onCancelClick={onCancelClick}
          onForceFailureClick={onForceFailureClick}
          onPauseClick={onPauseClick}
          onRemoveClick={onRemoveClick}
          onResumeClick={onResumeClick}
          onRetryClick={onRetryClick}
          projectPath={projectPath}
          setIsContextMenuOpen={setIsContextMenuOpen}
          showViewLogFile={false}
        />
      </ControlsWrapper>
    </RelativeContainer>
  );
}

const RelativeContainer = styled.div<{ $isSelected: boolean }>`
  background-color: ${(p) =>
    p.$isSelected && theme.colors.background.interactive.hover};
  border-radius: ${theme.spaces[8]};
  margin-bottom: ${theme.spaces[8]};
  position: relative;
  transition: background-color 0.3s ${theme.transitions.eases.primary};
  width: 100%;
  &:hover {
    background-color: ${theme.colors.background.interactive.hover};
  }
`;

const ControlsWrapper = styled.div`
  position: absolute;
  right: ${theme.spaces[12]};
  top: 19px;
`;

const Container = styled.div`
  border-radius: ${theme.spaces[8]};
  cursor: pointer;
  display: flex;
  padding: ${theme.spaces[12]};

  .status {
    margin-top: ${theme.spaces[2]};
    white-space: nowrap;

    .${ARROW_ICON_CLASSNAME} {
      /* Optically center arrow icon beneath job name */
      margin-left: -3px;
      margin-right: ${theme.spaces[2]};
      margin-top: -2px;
    }
  }

  .status {
    align-items: center;
    display: flex;
  }

  .progress-bar {
    margin-top: ${theme.spaces[8]};
    margin-bottom: ${theme.spaces[8]};
  }

  .checkmark {
    margin-right: ${theme.spaces[4]};
    vertical-align: middle;
  }

  width: 100%;
`;

const Wrapper = styled.div`
  overflow: hidden;
  width: 100%;
`;

const TitleContainer = styled.div<{ $isCancelled?: boolean }>`
  overflow: hidden;
  position: relative;
  text-decoration: ${(p) => p.$isCancelled && 'line-through'};
  text-decoration-color: ${theme.colors.text.secondary};
  width: fit-content;
`;

const ContextWrapper = styled.div`
  display: flex;
  margin-left: auto;
`;

const PillContainer = styled.div`
  align-items: center;
  background-color: ${theme.colors.background.interactive.error};
  border-radius: ${theme.spaces[16]};
  display: flex;
  height: ${theme.spaces[24]};
  justify-content: center;
  line-height: 1;
  margin: 0 ${theme.spaces[8]} 0 0;
  padding: 0 ${theme.spaces[8]};
`;
